import {
    type GroupChannel,
    GroupChannelHandler,
} from '@sendbird/chat/groupChannel';
import type {
    BaseMessage,
    FileMessage,
    UserMessage,
} from '@sendbird/chat/message';
import { useEffect, useState } from 'react';
import type { SharedValue } from 'react-native-reanimated';
import { BodySmallRegular, Spinner, View } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import type { Guide } from '../../guidance/types/Guide';
import { captureException } from '../../logger/Logger';
import useGuideMessaging from '../hooks/useGuideMessaging';
import useSendbird from '../hooks/useSendbird';
import type { MessagingSnapshotContext } from '../types/GuideMessaging';
import LatestGuideMessageCta from './LatestGuideMessageCta';
import MessagingGetStartedCta from './MessagingGetStartedCta';

interface MessagingSnapshotProps {
    guide?: Guide | null;
    context: MessagingSnapshotContext;
    scrollOffset: SharedValue<number>;
}

const MessagingSnapshot = ({
    guide,
    context,
    scrollOffset,
}: MessagingSnapshotProps) => {
    const user = useAuthedUser();
    const { guideMessaging, isLoading: isLoadingGuideMessaging } =
        useGuideMessaging();
    const { sdk: sendbirdSdk, isLoading: isLoadingSendbird } = useSendbird();
    const [isLoadingMessages, setIsLoadingMessages] = useState(true);
    const [latestMessage, setLatestMessage] = useState<BaseMessage | null>(
        null,
    );
    const [sendbirdChannel, setSendbirdChannel] = useState<GroupChannel | null>(
        null,
    );

    // Initially set the Sendbird channel and fetch the latest message from the guide.
    useEffect(() => {
        // Wait until we are connected to Sendbird.
        if (isLoadingGuideMessaging || isLoadingSendbird) {
            return;
        }

        // Handle failures to load guide messaging data and/or Sendbird.
        if (!guideMessaging || !sendbirdSdk) {
            setIsLoadingMessages(false);
            return;
        }

        const fetchLatestGuideMessage = async () => {
            const channel = await sendbirdSdk.groupChannel.getChannel(
                guideMessaging.currentChat.channel.channelUrl,
            );

            setSendbirdChannel(channel);

            const messages = await channel.getMessagesByTimestamp(Date.now(), {
                prevResultSize: 1,
                nextResultSize: 0,
                reverse: true,
            });

            setLatestMessage(messages[0] ?? null);
            setIsLoadingMessages(false);
        };

        fetchLatestGuideMessage().catch((e) => {
            captureException(e);
            setIsLoadingMessages(false);
        });
    }, [
        isLoadingGuideMessaging,
        isLoadingSendbird,
        sendbirdSdk,
        guideMessaging,
    ]);

    // Add a listener for new messages to update this component or child components accordingly.
    useEffect(() => {
        if (isLoadingGuideMessaging || isLoadingSendbird) {
            return;
        }

        if (!sendbirdChannel || !sendbirdSdk) {
            return;
        }

        const channelEventHandler = new GroupChannelHandler({
            onMessageReceived: (_, message: BaseMessage) => {
                const sendableMessage = message as UserMessage | FileMessage;
                if (sendableMessage.sender?.userId === guide?.sendbirdUserId) {
                    setLatestMessage(message);
                }
            },
        });

        const UNIQUE_HANDLER_ID = `${user.id}-${context}-onMessageReceived`;

        sendbirdSdk.groupChannel.addGroupChannelHandler(
            UNIQUE_HANDLER_ID,
            channelEventHandler,
        );

        return () => {
            sendbirdSdk.groupChannel.removeGroupChannelHandler(
                UNIQUE_HANDLER_ID,
            );
        };
    }, [
        sendbirdChannel,
        isLoadingGuideMessaging,
        isLoadingSendbird,
        sendbirdSdk,
    ]);

    return (
        <View marginTop={0} $sm={{ flex: 1 }}>
            {isLoadingMessages ? (
                <Spinner paddingVertical={36} />
            ) : !guideMessaging || sendbirdSdk == null ? (
                <BodySmallRegular paddingBottom={36}>
                    Guide messaging is currently unavailable.
                </BodySmallRegular>
            ) : latestMessage == null ? (
                <MessagingGetStartedCta
                    guide={guide}
                    scrollOffset={scrollOffset}
                />
            ) : 'sender' in latestMessage ? (
                <LatestGuideMessageCta
                    guide={guide}
                    message={latestMessage as UserMessage | FileMessage}
                />
            ) : (
                <></>
            )}
        </View>
    );
};

export default MessagingSnapshot;
